import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import Logo from './Logo';
import LegalFooter from './LegalFooter';
import { ADMIN_INSPECTOR_FEATURE_ENABLED, isAdminInspectorAllowed } from '../utils/adminInspector';
import { Button } from '../ui';

// Hide Audit UI by default; enable only when explicitly turned on at build time.
const AUDIT_FEATURE_ENABLED = String(process.env.REACT_APP_FEATURE_AUDIT || '').trim() === '1';

/**
 * OwnerShell
 *
 * Shared layout wrapper for all owner-facing pages to ensure the sidebar nav is
 * consistent across the app. (Nominee pages use NomineeShell instead.)
 *
 * Security note: admin gating here is UI-only; server-side authorization must
 * still be enforced on admin APIs.
 */
export default function OwnerShell({ children }) {
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showAdminInspectorLink = useMemo(() => (
    ADMIN_INSPECTOR_FEATURE_ENABLED && isAdminInspectorAllowed(currentUser)
  ), [currentUser]);

  // Audit is admin-only and feature-flagged; we don't want customers to see it yet.
  const showAuditLink = useMemo(() => (
    AUDIT_FEATURE_ENABLED && isAdminInspectorAllowed(currentUser)
  ), [currentUser]);

  const sidebarHeight = isMobile ? '100dvh' : '100vh';
  const sidebarStyle = {
    width: 220,
    background: 'var(--dusk-700)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    boxShadow: '2px 0 12px rgba(30,40,60,0.04)',
    position: 'fixed',
    left: 0,
    top: 0,
    height: sidebarHeight,
    zIndex: 100,
    padding: 0,
    overflow: 'hidden',
  };
  const mobileSidebarStyle = {
    ...sidebarStyle,
    width: 180,
    minWidth: 0,
    maxWidth: '80vw',
    transition: 'transform 0.3s',
    transform: 'translateX(-100%)',
    zIndex: 2200,
  };
  const mobileSidebarOpenStyle = {
    ...mobileSidebarStyle,
    transform: 'translateX(0)',
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--mist-100)' }}>
      {/* Mobile hamburger */}
      {isMobile && !sidebarOpen && (
        <Button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          variant="primary"
          style={{ position: 'fixed', top: 18, left: 18, zIndex: 2100, fontSize: 22, padding: '10px 12px' }}
          aria-label="Open menu"
        >
          ☰
        </Button>
      )}

      {/* Mobile backdrop (closes menu) */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(30,40,60,0.35)',
            zIndex: 2100,
          }}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <nav
        style={
          isMobile
            ? (sidebarOpen ? mobileSidebarOpenStyle : mobileSidebarStyle)
            : sidebarStyle
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          {/* Scrollable menu area */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              padding: '32px 0 0 0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 18px 24px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', flex: 1, minWidth: 0 }}>
                <Logo size={isMobile ? 28 : 36} variant={isMobile ? 'compact' : 'full'} />
              </div>
              {isMobile && (
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  aria-label="Close menu"
                  style={{
                    marginLeft: 8,
                    background: 'rgba(255,255,255,0.10)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    padding: '8px 10px',
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            {/* User indicator */}
            <div style={{ background: 'var(--sage-600)', color: '#fff', borderRadius: 12, padding: '10px 16px', margin: '0 18px 18px 18px', fontSize: 15, fontWeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, boxShadow: 'var(--shadow-soft)', overflowWrap: 'anywhere', maxWidth: 'calc(100% - 36px)' }}>
              <span>Logged in as:</span>
              <span style={{ fontWeight: 700, lineHeight: 1.2 }}>{currentUser?.name}</span>
              <span
                title={currentUser?.email || ''}
                style={{
                  fontSize: 13,
                  opacity: 0.85,
                  lineHeight: 1.2,
                  maxWidth: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {currentUser?.email}
              </span>
              {currentUser?.role && <span style={{ fontSize: 13, opacity: 0.7 }}>({currentUser.role})</span>}
            </div>

            {/* Dashboard */}
            <div style={{ marginBottom: 24 }}>
              <Button
                onClick={() => { navigate('/dashboard'); setSidebarOpen(false); }}
                variant="sidebar"
                active={location.pathname === '/dashboard'}
              >
                Dashboard
              </Button>
            </div>

            {/* Assets Section */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#888', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Assets</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  onClick={() => { navigate('/financial-assets'); setSidebarOpen(false); }}
                  variant="sidebar"
                  active={location.pathname === '/financial-assets'}
                >
                  Financial Assets
                </Button>
                <Button
                  onClick={() => { navigate('/legal-assets'); setSidebarOpen(false); }}
                  variant="sidebar"
                  active={location.pathname === '/legal-assets'}
                >
                  Legal Assets
                </Button>
                <Button
                  onClick={() => { navigate('/other-assets'); setSidebarOpen(false); }}
                  variant="sidebar"
                  active={location.pathname === '/other-assets'}
                >
                  Other Assets
                </Button>
              </div>
            </div>

            {/* Account Section */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#888', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Account</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  onClick={() => { navigate('/account'); setSidebarOpen(false); }}
                  variant="sidebar"
                  active={location.pathname === '/account'}
                >
                  Account
                </Button>
                {showAuditLink && (
                  <Button
                    onClick={() => { navigate('/audit'); setSidebarOpen(false); }}
                    variant="sidebar"
                    active={location.pathname === '/audit'}
                  >
                    Audit
                  </Button>
                )}
              </div>
            </div>

            {/* Admin Section */}
            {showAdminInspectorLink && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#888', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Admin</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    onClick={() => { navigate('/admin/inspector'); setSidebarOpen(false); }}
                    variant="sidebar"
                    active={location.pathname === '/admin/inspector'}
                  >
                    Inspector
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Pinned bottom actions */}
          <div
            style={{
              flex: '0 0 auto',
              padding: '12px 0 calc(12px + env(safe-area-inset-bottom, 0px)) 0',
              background: 'var(--dusk-700)',
              borderTop: '1px solid rgba(255,255,255,0.10)',
            }}
          >
          <Button
            type="button"
            onClick={() => {
              // Match existing logout UX: explicit logout forces a fresh Hosted UI login.
              try { window.sessionStorage.setItem('force_reauth', '1'); } catch {}
              setCurrentUser(null);
              navigate('/logout');
              setSidebarOpen(false);
            }}
            variant="sidebar"
          >
            Log out
          </Button>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : 220, padding: isMobile ? '76px 0 0 0' : '0 0 0 0', minHeight: '100vh', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1 }}>{children}</div>
        {/* Persistent transparency footer (UK GDPR Art. 13). */}
        <LegalFooter />
      </div>
    </div>
  );
}

